import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, X, Upload, User, Phone, Home, Activity } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';
import { UserProfile, UpdateUserProfileData } from '../types';
import { validateImage, createPreviewUrl, cleanupPreviewUrl } from '../utils/imageUtils';
import {
  CARE_TYPES,
  HOUSE_TYPE_OPTIONS,
  BOOKING_FOR_OPTIONS,
  GENDER_OPTIONS,
  MOBILITY_OPTIONS,
  MENTAL_CONDITION_OPTIONS,
  CARE_SHIFT_OPTIONS,
  CARE_FREQUENCY_OPTIONS,
  CAREGIVER_GENDER_OPTIONS,
  RELATIONSHIP_OPTIONS
} from '../constants';

interface EditUserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile;
  onSubmit: (data: UpdateUserProfileData) => Promise<{ success: boolean; error?: string }>;
  onSuccess: () => void;
}

export const EditUserProfileModal: React.FC<EditUserProfileModalProps> = ({
  isOpen,
  onClose,
  profile,
  onSubmit,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [formData, setFormData] = useState<UpdateUserProfileData>({
    name: profile.name || '',
    phone: profile.profile?.phone || '',
    bio: profile.profile?.bio || '',
    address: profile.address || '',
    city: profile.city || '',
    postalCode: profile.postalCode || '',
    houseType: profile.houseType || '',
    floorNumber: profile.floorNumber || '',
    hasLift: profile.hasLift || false,
    petsAtHome: profile.petsAtHome || false,
    bookingFor: profile.bookingFor || '',
    recipientName: profile.recipientName || '',
    recipientAge: profile.recipientAge || 0,
    recipientGender: profile.recipientGender || '',
    height: profile.height || '',
    weight: profile.weight || '',
    mobilityStatus: profile.mobilityStatus || '',
    medicalConditions: profile.medicalConditions || [],
    allergies: profile.allergies || '',
    currentMedications: profile.currentMedications || '',
    requiresMedicalEquipment: profile.requiresMedicalEquipment || false,
    equipmentDetails: profile.equipmentDetails || '',
    mentalCondition: profile.mentalCondition || '',
    specialInstructions: profile.specialInstructions || '',
    preferredCareTypes: profile.preferredCareTypes || [],
    careShift: profile.careShift || '',
    careFrequency: profile.careFrequency || '',
    preferredCaregiverGender: profile.preferredCaregiverGender || '',
    languagesPreferred: profile.languagesPreferred || [],
    emergencyContactName: profile.emergencyContactName || '',
    emergencyContactPhone: profile.emergencyContactPhone || '',
    relationship: profile.relationship || '',
    photo: null,
  });

  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [photoPreview, setPhotoPreview] = useState<string | null>(profile.profile?.avatar || null);
  const [medicalConditionInput, setMedicalConditionInput] = useState('');
  const [languageInput, setLanguageInput] = useState('');

  useEffect(() => {
    return () => {
      if (photoPreview && photoPreview.startsWith('blob:')) {
        cleanupPreviewUrl(photoPreview);
      }
    };
  }, [photoPreview]);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: profile.name || '',
        phone: profile.profile?.phone || '',
        bio: profile.profile?.bio || '',
        address: profile.address || '',
        city: profile.city || '',
        postalCode: profile.postalCode || '',
        houseType: profile.houseType || '',
        floorNumber: profile.floorNumber || '',
        hasLift: profile.hasLift || false,
        petsAtHome: profile.petsAtHome || false,
        bookingFor: profile.bookingFor || '',
        recipientName: profile.recipientName || '',
        recipientAge: profile.recipientAge || 0,
        recipientGender: profile.recipientGender || '',
        height: profile.height || '',
        weight: profile.weight || '',
        mobilityStatus: profile.mobilityStatus || '',
        medicalConditions: profile.medicalConditions || [],
        allergies: profile.allergies || '',
        currentMedications: profile.currentMedications || '',
        requiresMedicalEquipment: profile.requiresMedicalEquipment || false,
        equipmentDetails: profile.equipmentDetails || '',
        mentalCondition: profile.mentalCondition || '',
        specialInstructions: profile.specialInstructions || '',
        preferredCareTypes: profile.preferredCareTypes || [],
        careShift: profile.careShift || '',
        careFrequency: profile.careFrequency || '',
        preferredCaregiverGender: profile.preferredCaregiverGender || '',
        languagesPreferred: profile.languagesPreferred || [],
        emergencyContactName: profile.emergencyContactName || '',
        emergencyContactPhone: profile.emergencyContactPhone || '',
        relationship: profile.relationship || '',
        photo: null,
      });
      setPhotoPreview(profile.profile?.avatar || null);
      setCurrentStep(1);
      setError('');
      setStepErrors({});
    }
  }, [isOpen, profile]);

  const handleInputChange = (field: keyof UpdateUserProfileData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (stepErrors[field]) {
      setStepErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImage(file);
    if (!validation.isValid) {
      setStepErrors(prev => ({ ...prev, photo: validation.error || 'Invalid image' }));
      return;
    }

    if (photoPreview && photoPreview.startsWith('blob:')) {
      cleanupPreviewUrl(photoPreview);
    }

    const preview = createPreviewUrl(file);
    setPhotoPreview(preview);
    handleInputChange('photo', file);
    setStepErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.photo;
      return newErrors;
    });
  };

  const handleRemovePhoto = () => {
    if (photoPreview && photoPreview.startsWith('blob:')) {
      cleanupPreviewUrl(photoPreview);
    }
    setPhotoPreview(null);
    handleInputChange('photo', null);
  };

  const addMedicalCondition = () => {
    if (medicalConditionInput.trim()) {
      handleInputChange('medicalConditions', [...(formData.medicalConditions || []), medicalConditionInput.trim()]);
      setMedicalConditionInput('');
    }
  };

  const removeMedicalCondition = (index: number) => {
    handleInputChange('medicalConditions', (formData.medicalConditions || []).filter((_, i) => i !== index));
  };

  const addLanguage = () => {
    if (languageInput.trim()) {
      handleInputChange('languagesPreferred', [...(formData.languagesPreferred || []), languageInput.trim()]);
      setLanguageInput('');
    }
  };

  const removeLanguage = (index: number) => {
    handleInputChange('languagesPreferred', (formData.languagesPreferred || []).filter((_, i) => i !== index));
  };

  const toggleCareType = (careType: string) => {
    const currentCareTypes = formData.preferredCareTypes || [];
    if (currentCareTypes.includes(careType)) {
      handleInputChange('preferredCareTypes', currentCareTypes.filter(ct => ct !== careType));
    } else {
      handleInputChange('preferredCareTypes', [...currentCareTypes, careType]);
    }
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.name?.trim()) errors.name = 'Name is required';
      if (!formData.phone?.trim()) errors.phone = 'Phone is required';
    } else if (step === 2) {
      if (!formData.address?.trim()) errors.address = 'Address is required';
      if (!formData.city?.trim()) errors.city = 'City is required';
      if (!formData.postalCode?.trim()) errors.postalCode = 'Postal code is required';
      if (!formData.houseType) errors.houseType = 'House type is required';
    } else if (step === 3) {
      if (!formData.bookingFor) errors.bookingFor = 'Please specify who the booking is for';
      if (!formData.recipientName?.trim()) errors.recipientName = 'Recipient name is required';
      if (!formData.recipientAge || formData.recipientAge <= 0) errors.recipientAge = 'Valid age is required';
      if (!formData.recipientGender) errors.recipientGender = 'Gender is required';
      if (!formData.mobilityStatus) errors.mobilityStatus = 'Mobility status is required';
      if (!formData.medicalConditions || formData.medicalConditions.length === 0) {
        errors.medicalConditions = 'At least one medical condition is required';
      }
    } else if (step === 4) {
      if (!formData.emergencyContactName?.trim()) errors.emergencyContactName = 'Emergency contact name is required';
      if (!formData.emergencyContactPhone?.trim()) errors.emergencyContactPhone = 'Emergency contact phone is required';
      if (!formData.relationship) errors.relationship = 'Relationship is required';
      if (!formData.preferredCareTypes || formData.preferredCareTypes.length === 0) {
        errors.preferredCareTypes = 'At least one care type must be selected';
      }
      if (!formData.careShift) errors.careShift = 'Care shift is required';
      if (!formData.careFrequency) errors.careFrequency = 'Care frequency is required';
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      setError('');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    setError('');

    try {
      console.log('=== EditUserProfileModal: Submitting Profile Update ===');
      console.log('FormData being submitted:', {
        medicalConditions: formData.medicalConditions,
        languagesPreferred: formData.languagesPreferred,
        preferredCareTypes: formData.preferredCareTypes,
        ...formData
      });

      const result = await onSubmit(formData);

      console.log('Profile update result:', result);

      if (result.success) {
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Failed to update profile. Please try again.');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            step < currentStep ? 'bg-blue-600' :
            step === currentStep ? 'bg-blue-600' :
            'bg-gray-200'
          } text-white font-semibold`}>
            {step < currentStep ? <Check size={20} /> : step}
          </div>
          {step < 4 && (
            <div className={`w-16 h-1 ${step < currentStep ? 'bg-blue-600' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <User className="text-blue-600" size={24} />
        <h3 className="text-xl font-semibold text-gray-900">Personal Information</h3>
      </div>

      <div className="flex flex-col items-center mb-6">
        <div className="relative">
          {photoPreview ? (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Profile preview"
                className="w-32 h-32 rounded-full object-cover border-4 border-blue-100"
              />
              <button
                type="button"
                onClick={handleRemovePhoto}
                className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center border-4 border-gray-200">
              <User size={48} className="text-gray-400" />
            </div>
          )}
        </div>
        <label className="mt-4 cursor-pointer">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium">
            <Upload size={16} />
            {photoPreview ? 'Change Photo' : 'Upload Photo'}
          </span>
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            className="hidden"
          />
        </label>
        {stepErrors.photo && (
          <p className="text-red-500 text-sm mt-2">{stepErrors.photo}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Full Name <span className="text-red-500">*</span>
        </label>
        <Input
          value={formData.name || ''}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="Enter your full name"
        />
        {stepErrors.name && (
          <p className="text-red-500 text-sm mt-1">{stepErrors.name}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <Input
          value={formData.phone || ''}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          placeholder="Enter your phone number"
        />
        {stepErrors.phone && (
          <p className="text-red-500 text-sm mt-1">{stepErrors.phone}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Bio (Optional)
        </label>
        <textarea
          value={formData.bio || ''}
          onChange={(e) => handleInputChange('bio', e.target.value)}
          placeholder="Tell us a bit about yourself"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows={3}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <Home className="text-blue-600" size={24} />
        <h3 className="text-xl font-semibold text-gray-900">Home Address</h3>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address <span className="text-red-500">*</span>
        </label>
        <Input
          value={formData.address || ''}
          onChange={(e) => handleInputChange('address', e.target.value)}
          placeholder="Enter your street address"
        />
        {stepErrors.address && (
          <p className="text-red-500 text-sm mt-1">{stepErrors.address}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.city || ''}
            onChange={(e) => handleInputChange('city', e.target.value)}
            placeholder="Enter your city"
          />
          {stepErrors.city && (
            <p className="text-red-500 text-sm mt-1">{stepErrors.city}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Postal Code <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.postalCode || ''}
            onChange={(e) => handleInputChange('postalCode', e.target.value)}
            placeholder="Enter postal code"
          />
          {stepErrors.postalCode && (
            <p className="text-red-500 text-sm mt-1">{stepErrors.postalCode}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          House Type <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.houseType || ''}
          onChange={(e) => handleInputChange('houseType', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select house type</option>
          {HOUSE_TYPE_OPTIONS.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        {stepErrors.houseType && (
          <p className="text-red-500 text-sm mt-1">{stepErrors.houseType}</p>
        )}
      </div>

      {(formData.houseType === 'Apartment' || formData.houseType === 'Flat') && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Floor Number (Optional)
            </label>
            <Input
              value={formData.floorNumber || ''}
              onChange={(e) => handleInputChange('floorNumber', e.target.value)}
              placeholder="e.g., 3rd floor"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hasLift"
              checked={formData.hasLift || false}
              onChange={(e) => handleInputChange('hasLift', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="hasLift" className="text-sm text-gray-700">
              Building has a lift/elevator
            </label>
          </div>
        </>
      )}

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="petsAtHome"
          checked={formData.petsAtHome || false}
          onChange={(e) => handleInputChange('petsAtHome', e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="petsAtHome" className="text-sm text-gray-700">
          Pets at home
        </label>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
      <div className="flex items-center gap-3 mb-4">
        <Activity className="text-blue-600" size={24} />
        <h3 className="text-xl font-semibold text-gray-900">Care Recipient & Medical Info</h3>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800">Please provide details about the person receiving care</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Booking For <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.bookingFor || ''}
          onChange={(e) => handleInputChange('bookingFor', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select who the care is for</option>
          {BOOKING_FOR_OPTIONS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        {stepErrors.bookingFor && (
          <p className="text-red-500 text-sm mt-1">{stepErrors.bookingFor}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipient Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.recipientName || ''}
            onChange={(e) => handleInputChange('recipientName', e.target.value)}
            placeholder="Full name"
          />
          {stepErrors.recipientName && (
            <p className="text-red-500 text-sm mt-1">{stepErrors.recipientName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Age <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            value={formData.recipientAge || ''}
            onChange={(e) => handleInputChange('recipientAge', parseInt(e.target.value) || 0)}
            placeholder="Age"
            min="0"
          />
          {stepErrors.recipientAge && (
            <p className="text-red-500 text-sm mt-1">{stepErrors.recipientAge}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gender <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.recipientGender || ''}
            onChange={(e) => handleInputChange('recipientGender', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select</option>
            {GENDER_OPTIONS.map(gender => (
              <option key={gender} value={gender}>{gender}</option>
            ))}
          </select>
          {stepErrors.recipientGender && (
            <p className="text-red-500 text-sm mt-1">{stepErrors.recipientGender}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Height (Optional)
          </label>
          <Input
            value={formData.height || ''}
            onChange={(e) => handleInputChange('height', e.target.value)}
            placeholder="e.g., 5.6ft"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weight (Optional)
          </label>
          <Input
            value={formData.weight || ''}
            onChange={(e) => handleInputChange('weight', e.target.value)}
            placeholder="e.g., 70kg"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Medical Conditions <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2 mb-2">
          <Input
            value={medicalConditionInput}
            onChange={(e) => setMedicalConditionInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMedicalCondition())}
            placeholder="Type condition and press Enter"
          />
          <Button onClick={addMedicalCondition} variant="secondary" size="sm">Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(formData.medicalConditions || []).map((condition, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
            >
              {condition}
              <button
                type="button"
                onClick={() => removeMedicalCondition(index)}
                className="hover:text-blue-900"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
        {stepErrors.medicalConditions && (
          <p className="text-red-500 text-sm mt-1">{stepErrors.medicalConditions}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Allergies (Optional)
        </label>
        <textarea
          value={formData.allergies || ''}
          onChange={(e) => handleInputChange('allergies', e.target.value)}
          placeholder="List any allergies"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows={2}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Current Medications (Optional)
        </label>
        <textarea
          value={formData.currentMedications || ''}
          onChange={(e) => handleInputChange('currentMedications', e.target.value)}
          placeholder="List current medications"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows={2}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mobility Status <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.mobilityStatus || ''}
          onChange={(e) => handleInputChange('mobilityStatus', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select mobility status</option>
          {MOBILITY_OPTIONS.map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        {stepErrors.mobilityStatus && (
          <p className="text-red-500 text-sm mt-1">{stepErrors.mobilityStatus}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="requiresMedicalEquipment"
          checked={formData.requiresMedicalEquipment || false}
          onChange={(e) => handleInputChange('requiresMedicalEquipment', e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="requiresMedicalEquipment" className="text-sm text-gray-700">
          Requires medical equipment
        </label>
      </div>

      {formData.requiresMedicalEquipment && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Equipment Details
          </label>
          <textarea
            value={formData.equipmentDetails || ''}
            onChange={(e) => handleInputChange('equipmentDetails', e.target.value)}
            placeholder="List required medical equipment"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={2}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mental Condition (Optional)
        </label>
        <select
          value={formData.mentalCondition || ''}
          onChange={(e) => handleInputChange('mentalCondition', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select mental condition</option>
          {MENTAL_CONDITION_OPTIONS.map(condition => (
            <option key={condition} value={condition}>{condition}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Special Instructions (Optional)
        </label>
        <textarea
          value={formData.specialInstructions || ''}
          onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
          placeholder="Any special care instructions or notes"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows={3}
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
      <div className="flex items-center gap-3 mb-4">
        <Phone className="text-blue-600" size={24} />
        <h3 className="text-xl font-semibold text-gray-900">Emergency Contacts & Care Preferences</h3>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-sm text-amber-800 font-semibold">Emergency Contact Information</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Emergency Contact Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.emergencyContactName || ''}
            onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
            placeholder="Contact name"
          />
          {stepErrors.emergencyContactName && (
            <p className="text-red-500 text-sm mt-1">{stepErrors.emergencyContactName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Emergency Contact Phone <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.emergencyContactPhone || ''}
            onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
            placeholder="Contact phone"
          />
          {stepErrors.emergencyContactPhone && (
            <p className="text-red-500 text-sm mt-1">{stepErrors.emergencyContactPhone}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Relationship to Recipient <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.relationship || ''}
          onChange={(e) => handleInputChange('relationship', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select relationship</option>
          {RELATIONSHIP_OPTIONS.map(rel => (
            <option key={rel} value={rel}>{rel}</option>
          ))}
        </select>
        {stepErrors.relationship && (
          <p className="text-red-500 text-sm mt-1">{stepErrors.relationship}</p>
        )}
      </div>

      <div className="border-t pt-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800 font-semibold">Care Preferences</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Care Types <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {CARE_TYPES.map((careType) => (
              <label
                key={careType}
                className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={(formData.preferredCareTypes || []).includes(careType)}
                  onChange={() => toggleCareType(careType)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{careType}</span>
              </label>
            ))}
          </div>
          {stepErrors.preferredCareTypes && (
            <p className="text-red-500 text-sm mt-1">{stepErrors.preferredCareTypes}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Care Shift <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.careShift || ''}
              onChange={(e) => handleInputChange('careShift', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select shift</option>
              {CARE_SHIFT_OPTIONS.map(shift => (
                <option key={shift} value={shift}>{shift}</option>
              ))}
            </select>
            {stepErrors.careShift && (
              <p className="text-red-500 text-sm mt-1">{stepErrors.careShift}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Care Frequency <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.careFrequency || ''}
              onChange={(e) => handleInputChange('careFrequency', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select frequency</option>
              {CARE_FREQUENCY_OPTIONS.map(freq => (
                <option key={freq} value={freq}>{freq}</option>
              ))}
            </select>
            {stepErrors.careFrequency && (
              <p className="text-red-500 text-sm mt-1">{stepErrors.careFrequency}</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Caregiver Gender (Optional)
          </label>
          <select
            value={formData.preferredCaregiverGender || ''}
            onChange={(e) => handleInputChange('preferredCaregiverGender', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select preference</option>
            {CAREGIVER_GENDER_OPTIONS.map(gender => (
              <option key={gender} value={gender}>{gender}</option>
            ))}
          </select>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Languages Preferred (Optional)
          </label>
          <div className="flex gap-2 mb-2">
            <Input
              value={languageInput}
              onChange={(e) => setLanguageInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
              placeholder="Type language and press Enter"
            />
            <Button onClick={addLanguage} variant="secondary" size="sm">Add</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(formData.languagesPreferred || []).map((language, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
              >
                {language}
                <button
                  type="button"
                  onClick={() => removeLanguage(index)}
                  className="hover:text-blue-900"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return null;
    }
  };

  if (showSuccessModal) {
    return (
      <Modal isOpen={true} onClose={() => {}} title="">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="text-green-600" size={32} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Profile Updated Successfully!</h3>
          <p className="text-gray-600">Your profile has been updated with the latest information.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Your Profile">
      <div className="max-h-[70vh] overflow-y-auto px-1">
        {renderStepIndicator()}
        {renderCurrentStep()}

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex gap-3 mt-8 pt-6 border-t">
          <Button
            onClick={onClose}
            variant="secondary"
            disabled={loading}
          >
            Cancel
          </Button>
          {currentStep > 1 && (
            <Button
              onClick={handlePrevious}
              variant="secondary"
              disabled={loading}
              className="flex-1"
            >
              <ChevronLeft size={20} />
              Previous
            </Button>
          )}
          {currentStep < 4 ? (
            <Button
              onClick={handleNext}
              disabled={loading}
              className="flex-1"
            >
              Next
              <ChevronRight size={20} />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Updating Profile...' : 'Update Profile'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
